import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronRight, BarChart2, Trash2 } from 'lucide-react';
import { api } from '../../services/api';
import { getFlowStatConfig, FlowStatConfig } from '../../config/flowStatisticsConfig';

interface FlowCatalogEntry {
  id: string;
  name: string;
}

// Job-Typen aus dem alten Historie-System (Standalone-Tools)
const JOB_TYPE_LABELS: Record<string, string> = {
  huck_price_upload: 'Huck Preisupload',
  helo_price_upload: 'Helo Preisupdate',
  sporttore_import: 'SportTore Import',
  invoice_check_sporttore: 'SportTore Rechnungsprüfung',
  invoice_check_hela: 'HELA Rechnungsprüfung',
  jako_analysis: 'JAKO Analyse',
  teamshop_refund: 'Teamshop Rückvergütung',
  sales_analysis: 'Verkaufszahlenanalyse',
  vororder_analysis: 'Vororder-Analyse',
  rating: 'Kundenbewertung',
};

interface RecentRun {
  id: string;
  status: string;
  startedAt: string;
  completedAt: string | null;
  durationMs: number | null;
  resultSummary: Record<string, any> | null;
  params?: Record<string, any> | null;
}

interface CycleData {
  nextId: number;
  maxId: number;
  completedCycles: number;
  progressPercent: number;
  totalContacts: number;
  processedInCycle: number;
  lastCycleStartedAt: string | null;
  totalRuns: number;
}

interface UniqueRatings {
  total: number;
  fiveStar: number;
  oneStar: number;
  zeroStar: number;
}

interface StatisticsData {
  aggregated: Record<string, any>;
  recentRuns: RecentRun[];
  totalRuns: number;
  cycle?: CycleData | null;
  uniqueRatings?: UniqueRatings;
}

interface RunDetail {
  totalItems: number;
  items: any[];
  page: number;
  pageSize: number;
}

// ── Helpers ────────────────────────────────

const formatNumber = (n: number): string => {
  return n.toLocaleString('de-DE');
};

const formatDate = (d: string | null) => {
  if (!d) return '-';
  return new Date(d).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const formatDuration = (ms: number | null) => {
  if (!ms) return '-';
  if (ms < 60000) return `${(ms / 1000).toFixed(0)}s`;
  return `${Math.floor(ms / 60000)}m`;
};

// ── MetricCards ────────────────────────────

const MetricCards: React.FC<{ config: FlowStatConfig; data: Record<string, any> }> = ({ config, data }) => (
  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(config.cards.length, 5)}, 1fr)`, gap: '0.75rem', marginBottom: '1rem' }}>
    {config.cards.map(card => {
      let value = card.nestedKey
        ? (data[card.key] && typeof data[card.key] === 'object' ? data[card.key][card.nestedKey] : 0)
        : (data[card.key] ?? 0);
      if (typeof value !== 'number') value = 0;
      return (
        <div key={`${card.key}-${card.nestedKey || ''}`} className="bg-white rounded-lg p-4 border border-[#DAD9D9]" style={{
          borderLeft: `3px solid ${card.color || '#3B82F6'}`,
        }}>
          <div className="text-2xl font-bold text-[#1A1A1A]">{formatNumber(value)}</div>
          <div className="text-xs text-gray-500 mt-1">{card.label}</div>
        </div>
      );
    })}
  </div>
);

// ── HorizontalBarChart ─────────────────────

const HorizontalBarChart: React.FC<{ config: FlowStatConfig; data: Record<string, any> }> = ({ config, data }) => {
  if (!config.chart) return null;

  const chartData = config.chart.sourceKey && data[config.chart.sourceKey]
    ? data[config.chart.sourceKey] as Record<string, number>
    : data;

  const values = config.chart.bars.map(bar => ({
    ...bar,
    value: typeof chartData[bar.key] === 'number' ? chartData[bar.key] : 0,
  }));

  const maxValue = Math.max(...values.map(v => v.value), 1);

  return (
    <div className="bg-white rounded-lg p-4 border border-[#DAD9D9] mb-4">
      <div className="text-xs font-semibold text-[#1A1A1A] mb-3">
        {config.chart.title}
      </div>
      {values.map(bar => {
        const total = values.reduce((s, v) => s + v.value, 0);
        const pct = total > 0 ? Math.round((bar.value / total) * 100) : 0;
        return (
          <div key={bar.key} className="flex items-center gap-2 mb-2">
            <div className="w-[120px] text-xs text-gray-500 text-right flex-shrink-0">
              {bar.label}
            </div>
            <div className="flex-1 bg-gray-100 rounded h-5 overflow-hidden">
              <div style={{
                width: `${Math.max((bar.value / maxValue) * 100, bar.value > 0 ? 2 : 0)}%`,
                height: '100%', backgroundColor: bar.color, borderRadius: '4px',
                transition: 'width 0.5s ease',
              }} />
            </div>
            <div className="w-[100px] text-xs text-gray-500 flex-shrink-0">
              {formatNumber(bar.value)} ({pct}%)
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── CycleProgressBar ───────────────────────

const CycleProgressBar: React.FC<{ cycle: CycleData }> = ({ cycle }) => {
  const pct = Math.min(Math.max(cycle.progressPercent, 0), 100);
  const startedAt = cycle.lastCycleStartedAt
    ? new Date(cycle.lastCycleStartedAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '-';
  return (
    <div className="bg-white rounded-lg p-4 border border-[#DAD9D9] mb-4">
      <div className="flex items-center gap-3 mb-2">
        <div className="flex-1 bg-gray-100 rounded-full h-[18px] overflow-hidden">
          <div style={{
            width: `${pct}%`, height: '100%', backgroundColor: '#22c55e',
            borderRadius: '9999px', transition: 'width 0.5s ease',
            minWidth: pct > 0 ? '8px' : '0',
          }} />
        </div>
        <div className="font-bold text-[0.95rem] text-green-500 w-[46px] text-right flex-shrink-0">
          {pct}%
        </div>
      </div>
      <div className="text-xs text-gray-500">
        Aktueller Zyklus: {formatNumber(cycle.processedInCycle)} / {formatNumber(cycle.totalContacts)} IDs geprüft
        {' \u00b7 '}Nächste ID: {formatNumber(cycle.nextId)}
        {' \u00b7 '}Zyklus #{cycle.completedCycles + 1}
        {' \u00b7 '}Gestartet: {startedAt}
      </div>
    </div>
  );
};

// ── RunDetailTable ────────────────────────

const RunDetailTable: React.FC<{ flowId: string; executionId: string; config: FlowStatConfig }> = ({ flowId, executionId, config }) => {
  const [detail, setDetail] = useState<RunDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<Record<string, string>>({});

  useEffect(() => {
    setLoading(true);
    setFilters({});
    api.flows.getRunDetails(flowId, executionId).then(res => {
      setDetail(res.data);
    }).catch(() => {
      setDetail({ totalItems: 0, items: [], page: 1, pageSize: 0 });
    }).finally(() => setLoading(false));
  }, [flowId, executionId]);

  if (loading) return <div className="p-2 text-xs text-gray-400">Laden...</div>;
  if (!detail || detail.items.length === 0) return <div className="p-2 text-xs text-gray-400">Keine Detail-Daten verfügbar</div>;

  const columns: { key: string; label: string; width?: string }[] = config.detailColumns.length > 0
    ? config.detailColumns
    : Object.keys(detail.items[0] || {}).map(k => ({ key: k, label: k }));

  const filteredItems = detail.items.filter(item =>
    Object.entries(filters).every(([key, filterVal]) => {
      if (!filterVal) return true;
      const cellVal = String(item[key] ?? '').toLowerCase();
      return cellVal.includes(filterVal.toLowerCase());
    })
  );

  const hasActiveFilters = Object.values(filters).some(v => v);

  return (
    <div style={{ padding: '0.25rem 0.5rem 0.5rem 2rem' }}>
      <div className="text-[0.7rem] text-gray-400 mb-1">
        {hasActiveFilters
          ? `${filteredItems.length} von ${detail.totalItems} Einträge`
          : `${detail.totalItems} Einträge gesamt`}
      </div>
      <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
        <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse' }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 1, boxShadow: '0 1px 0 #DAD9D9' }}>
            <tr>
              {columns.map(col => (
                <th key={col.key} className="text-left px-2 py-1 text-gray-500 font-semibold bg-white" style={{ width: col.width, borderBottom: 'none' }}>{col.label}</th>
              ))}
            </tr>
            <tr>
              {columns.map(col => (
                <th key={`filter-${col.key}`} className="px-1 py-0.5 bg-white border-b border-[#DAD9D9]">
                  <input
                    type="text"
                    placeholder={col.label + '...'}
                    value={filters[col.key] || ''}
                    onChange={e => setFilters(prev => ({ ...prev, [col.key]: e.target.value }))}
                    onClick={e => e.stopPropagation()}
                    className="w-full text-[0.65rem] px-1 py-0.5 border border-[#DAD9D9] rounded-sm bg-white text-[#1A1A1A] outline-none"
                    style={{ boxSizing: 'border-box' }}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredItems.map((item, idx) => (
              <tr key={idx} className="border-b border-gray-100">
                {columns.map(col => (
                  <td key={col.key} className="px-2 py-1 text-[#1A1A1A]">
                    {renderCellValue(item[col.key])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

function renderCellValue(val: any): string {
  if (val === null || val === undefined) return '-';
  if (typeof val === 'number') return formatNumber(val);
  return String(val);
}

// ── RecentRunsTable ────────────────────────

const RecentRunsTable: React.FC<{ runs: RecentRun[]; flowId: string; config: FlowStatConfig; onDelete: () => void }> = ({ runs, flowId, config, onDelete }) => {
  const [expandedRun, setExpandedRun] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [filters, setFilters] = useState<Record<string, string>>({});

  const toggleAll = () => {
    if (selected.size === runs.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(runs.map(r => r.id)));
    }
  };

  const handleDeleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`${selected.size} Lauf${selected.size > 1 ? 'e' : ''} wirklich löschen?`)) return;
    setDeleting(true);
    try {
      await Promise.all(Array.from(selected).map(id => api.flows.deleteExecution(id)));
      setSelected(new Set());
      onDelete();
    } catch {
      // Toast wird von Axios-Interceptor gezeigt
    } finally {
      setDeleting(false);
    }
  };

  const summaryKeys = (config.tableCards || config.cards).slice(0, 4).map(c => ({
    key: c.nestedKey ? `${c.key}.${c.nestedKey}` : c.key,
    label: c.label,
    nestedKey: c.nestedKey,
    sourceKey: c.key,
  }));
  const stringCols = config.summaryStringColumns || [];

  const getSummaryValue = (summary: Record<string, any> | null, sk: typeof summaryKeys[0]): string => {
    if (!summary) return '-';
    if (sk.nestedKey) {
      const nested = summary[sk.sourceKey];
      if (nested && typeof nested === 'object') return formatNumber(nested[sk.nestedKey] ?? 0);
      return '-';
    }
    const val = summary[sk.sourceKey];
    return typeof val === 'number' ? formatNumber(val) : '-';
  };

  const getRunFilterValue = (run: RecentRun, filterKey: string): string => {
    if (filterKey === 'datum') return formatDate(run.startedAt);
    if (filterKey === 'dauer') return formatDuration(run.durationMs);
    if (filterKey === 'status') return run.status === 'completed' ? 'OK' : run.status === 'failed' ? 'Fehler' : run.status;
    const paramsCol = (config.paramsColumns || []).find(pc => `p_${pc.key}` === filterKey);
    if (paramsCol) return String(run.params?.[paramsCol.key] ?? '-');
    const sc = stringCols.find(c => `sc_${c.key}` === filterKey);
    if (sc) return String(run.resultSummary?.[sc.key] ?? '-');
    const sk = summaryKeys.find(s => s.key === filterKey);
    if (sk) return getSummaryValue(run.resultSummary, sk);
    return '';
  };

  const filteredRuns = runs.filter(run =>
    Object.entries(filters).every(([key, filterVal]) => {
      if (!filterVal) return true;
      const cellVal = getRunFilterValue(run, key).toLowerCase();
      return cellVal.includes(filterVal.toLowerCase());
    })
  );

  const hasActiveRunFilters = Object.values(filters).some(v => v);

  const statusBadge = (status: string) => {
    const styles: Record<string, React.CSSProperties> = {
      completed: { backgroundColor: '#D1FAE5', color: '#065F46' },
      failed: { backgroundColor: '#FEE2E2', color: '#991B1B' },
    };
    const labels: Record<string, string> = { completed: 'OK', failed: 'Fehler' };
    const style = styles[status] || { backgroundColor: '#F3F4F6', color: '#374151' };
    return <span style={{ ...style, padding: '1px 8px', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: 600 }}>{labels[status] || status}</span>;
  };

  const filterInputStyle = "w-full text-[0.65rem] px-1 py-0.5 border border-[#DAD9D9] rounded-sm bg-white text-[#1A1A1A] outline-none";

  return (
    <div className="bg-white rounded-lg border border-[#DAD9D9]">
      <div className="flex justify-between items-center px-4 pt-3 pb-2">
        <div className="text-xs font-semibold text-[#1A1A1A]">
          Letzte Läufe{hasActiveRunFilters ? ` (${filteredRuns.length} von ${runs.length})` : ''}
        </div>
        {selected.size > 0 && (
          <button
            onClick={handleDeleteSelected}
            disabled={deleting}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-red-300 bg-red-50 text-red-600 text-[0.7rem] font-semibold"
            style={{ cursor: deleting ? 'wait' : 'pointer', opacity: deleting ? 0.6 : 1 }}
          >
            <Trash2 size={12} />
            {deleting ? 'Lösche...' : `${selected.size} löschen`}
          </button>
        )}
      </div>
      <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse' }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 1, boxShadow: '0 1px 0 #DAD9D9' }}>
            <tr style={{ borderBottom: 'none' }}>
              <th className="w-8 px-2 py-1.5 text-center bg-white">
                <input type="checkbox" checked={runs.length > 0 && selected.size === runs.length} onChange={toggleAll}
                  style={{ cursor: 'pointer', accentColor: '#3B82F6' }} />
              </th>
              <th className="w-7 px-2 py-1.5 bg-white"></th>
              <th className="text-left px-2 py-1.5 text-gray-500 font-semibold bg-white">Datum</th>
              {(config.paramsColumns || []).map(pc => (
                <th key={`p_${pc.key}`} className="text-left px-2 py-1.5 text-gray-500 font-semibold bg-white">{pc.label}</th>
              ))}
              {summaryKeys.map(sk => (
                <th key={sk.key} className="text-right px-2 py-1.5 text-gray-500 font-semibold bg-white">{sk.label}</th>
              ))}
              {stringCols.map(sc => (
                <th key={`sc_${sc.key}`} className="text-left px-2 py-1.5 text-gray-500 font-semibold bg-white">{sc.label}</th>
              ))}
              <th className="text-right px-2 py-1.5 text-gray-500 font-semibold bg-white">Dauer</th>
              <th className="text-center px-2 py-1.5 text-gray-500 font-semibold bg-white">Status</th>
            </tr>
            <tr>
              <th className="px-1 py-0.5 bg-white border-b border-[#DAD9D9]"></th>
              <th className="px-1 py-0.5 bg-white border-b border-[#DAD9D9]"></th>
              <th className="px-1 py-0.5 bg-white border-b border-[#DAD9D9]">
                <input type="text" placeholder="Datum..." value={filters['datum'] || ''} onChange={e => setFilters(prev => ({ ...prev, datum: e.target.value }))} onClick={e => e.stopPropagation()} className={filterInputStyle} style={{ boxSizing: 'border-box' }} />
              </th>
              {(config.paramsColumns || []).map(pc => (
                <th key={`pf_${pc.key}`} className="px-1 py-0.5 bg-white border-b border-[#DAD9D9]">
                  <input type="text" placeholder={pc.label + '...'} value={filters[`p_${pc.key}`] || ''} onChange={e => setFilters(prev => ({ ...prev, [`p_${pc.key}`]: e.target.value }))} onClick={e => e.stopPropagation()} className={filterInputStyle} style={{ boxSizing: 'border-box' }} />
                </th>
              ))}
              {summaryKeys.map(sk => (
                <th key={`sf_${sk.key}`} className="px-1 py-0.5 bg-white border-b border-[#DAD9D9]">
                  <input type="text" placeholder={sk.label + '...'} value={filters[sk.key] || ''} onChange={e => setFilters(prev => ({ ...prev, [sk.key]: e.target.value }))} onClick={e => e.stopPropagation()} className={filterInputStyle} style={{ boxSizing: 'border-box' }} />
                </th>
              ))}
              {stringCols.map(sc => (
                <th key={`scf_${sc.key}`} className="px-1 py-0.5 bg-white border-b border-[#DAD9D9]">
                  <input type="text" placeholder={sc.label + '...'} value={filters[`sc_${sc.key}`] || ''} onChange={e => setFilters(prev => ({ ...prev, [`sc_${sc.key}`]: e.target.value }))} onClick={e => e.stopPropagation()} className={filterInputStyle} style={{ boxSizing: 'border-box' }} />
                </th>
              ))}
              <th className="px-1 py-0.5 bg-white border-b border-[#DAD9D9]">
                <input type="text" placeholder="Dauer..." value={filters['dauer'] || ''} onChange={e => setFilters(prev => ({ ...prev, dauer: e.target.value }))} onClick={e => e.stopPropagation()} className={filterInputStyle} style={{ boxSizing: 'border-box' }} />
              </th>
              <th className="px-1 py-0.5 bg-white border-b border-[#DAD9D9]">
                <input type="text" placeholder="Status..." value={filters['status'] || ''} onChange={e => setFilters(prev => ({ ...prev, status: e.target.value }))} onClick={e => e.stopPropagation()} className={filterInputStyle} style={{ boxSizing: 'border-box' }} />
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredRuns.map(run => {
              const isExpanded = expandedRun === run.id;
              const hasDetails = config.detailColumns.length > 0;
              return (
                <React.Fragment key={run.id}>
                  <tr
                    onClick={() => { if (hasDetails) setExpandedRun(isExpanded ? null : run.id); }}
                    className={`border-b ${isExpanded ? 'border-b-0' : 'border-gray-100'} ${hasDetails ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                    style={{ transition: 'background 0.15s' }}
                  >
                    <td className="px-2 py-1.5 text-center" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.has(run.id)} onChange={() => {
                        setSelected(prev => {
                          const next = new Set(prev);
                          if (next.has(run.id)) next.delete(run.id); else next.add(run.id);
                          return next;
                        });
                      }} style={{ cursor: 'pointer', accentColor: '#3B82F6' }} />
                    </td>
                    <td className="px-2 py-1.5 text-gray-400">
                      {hasDetails && (isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}
                    </td>
                    <td className="px-2 py-1.5 text-[#1A1A1A]">{formatDate(run.startedAt)}</td>
                    {(config.paramsColumns || []).map(pc => (
                      <td key={`p_${pc.key}`} className="px-2 py-1.5 text-[#1A1A1A] max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap">
                        {run.params?.[pc.key] ?? '-'}
                      </td>
                    ))}
                    {summaryKeys.map(sk => (
                      <td key={sk.key} className="text-right px-2 py-1.5 text-[#1A1A1A]">
                        {getSummaryValue(run.resultSummary, sk)}
                      </td>
                    ))}
                    {stringCols.map(sc => (
                      <td key={`sc_${sc.key}`} className="px-2 py-1.5 text-[#1A1A1A]" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {run.resultSummary?.[sc.key] ?? '-'}
                      </td>
                    ))}
                    <td className="text-right px-2 py-1.5 text-gray-500">{formatDuration(run.durationMs)}</td>
                    <td className="text-center px-2 py-1.5">{statusBadge(run.status)}</td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={summaryKeys.length + (config.paramsColumns?.length || 0) + stringCols.length + 5} style={{ padding: 0, borderBottom: '1px solid #DAD9D9' }}>
                        <RunDetailTable flowId={flowId} executionId={run.id} config={config} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {filteredRuns.length === 0 && runs.length > 0 && (
          <div className="p-8 text-center text-gray-400 text-xs">
            Keine Läufe entsprechen dem Filter
          </div>
        )}
        {runs.length === 0 && (
          <div className="p-8 text-center text-gray-400 text-xs">
            Noch keine Ausführungen im gewählten Zeitraum
          </div>
        )}
      </div>
    </div>
  );
};

// ── FlowStatisticsTab (Hauptkomponente) ───────

const isJobType = (id: string) => id.startsWith('job:');
const jobTypes = Object.keys(JOB_TYPE_LABELS);

// Typ-spezifische Stat-Configs für Job-Typen
const JOB_STAT_CONFIGS: Record<string, FlowStatConfig> = {
  'job:huck_price_upload': {
    cards: [
      { key: 'totalJobs', label: 'Läufe gesamt', color: '#3B82F6' },
      { key: 'successRate', label: 'Erfolgsrate %', color: '#8B5CF6' },
      { key: 'pricesUpdated', label: 'Preise aktualisiert', color: '#10B981' },
      { key: 'pricesFailed', label: 'Preise fehlgeschlagen', color: '#EF4444' },
    ],
    chart: {
      title: 'Ergebnis',
      bars: [
        { key: 'pricesUpdated', label: 'Aktualisiert', color: '#10B981' },
        { key: 'pricesFailed', label: 'Fehlgeschlagen', color: '#EF4444' },
      ],
    },
    detailColumns: [],
  },
  'job:helo_price_upload': {
    cards: [
      { key: 'totalJobs', label: 'Läufe gesamt', color: '#3B82F6' },
      { key: 'successRate', label: 'Erfolgsrate %', color: '#8B5CF6' },
      { key: 'pricesUpdated', label: 'Preise aktualisiert', color: '#10B981' },
      { key: 'pricesFailed', label: 'Preise fehlgeschlagen', color: '#EF4444' },
    ],
    chart: {
      title: 'Ergebnis',
      bars: [
        { key: 'pricesUpdated', label: 'Aktualisiert', color: '#10B981' },
        { key: 'pricesFailed', label: 'Fehlgeschlagen', color: '#EF4444' },
      ],
    },
    detailColumns: [],
  },
  'job:sporttore_import': {
    cards: [
      { key: 'totalJobs', label: 'Läufe gesamt', color: '#3B82F6' },
      { key: 'totalRows', label: 'Zeilen gesamt', color: '#6366F1' },
      { key: 'successCount', label: 'Erfolgreich', color: '#10B981' },
      { key: 'errorCount', label: 'Fehler', color: '#EF4444' },
    ],
    chart: {
      title: 'Ergebnis',
      bars: [
        { key: 'successCount', label: 'Erfolgreich', color: '#10B981' },
        { key: 'errorCount', label: 'Fehler', color: '#EF4444' },
      ],
    },
    detailColumns: [],
  },
  'job:invoice_check_sporttore': {
    cards: [
      { key: 'totalJobs', label: 'Läufe gesamt', color: '#3B82F6' },
      { key: 'totalChecked', label: 'Rechnungen geprüft', color: '#6366F1' },
      { key: 'matched', label: 'Übereinstimmend', color: '#10B981' },
      { key: 'mismatched', label: 'Abweichend', color: '#EF4444' },
    ],
    chart: {
      title: 'Prüfungsergebnis',
      bars: [
        { key: 'matched', label: 'Übereinstimmend', color: '#10B981' },
        { key: 'mismatched', label: 'Abweichend', color: '#EF4444' },
        { key: 'pdfNotFound', label: 'PDF fehlt', color: '#F59E0B' },
      ],
    },
    detailColumns: [],
  },
  'job:invoice_check_hela': {
    cards: [
      { key: 'totalJobs', label: 'Läufe gesamt', color: '#3B82F6' },
      { key: 'totalChecked', label: 'Rechnungen geprüft', color: '#6366F1' },
      { key: 'matched', label: 'Übereinstimmend', color: '#10B981' },
      { key: 'mismatched', label: 'Abweichend', color: '#EF4444' },
    ],
    chart: {
      title: 'Prüfungsergebnis',
      bars: [
        { key: 'matched', label: 'Übereinstimmend', color: '#10B981' },
        { key: 'mismatched', label: 'Abweichend', color: '#EF4444' },
        { key: 'pdfNotFound', label: 'PDF fehlt', color: '#F59E0B' },
      ],
    },
    detailColumns: [],
  },
  'job:jako_analysis': {
    cards: [
      { key: 'totalJobs', label: 'Läufe gesamt', color: '#3B82F6' },
      { key: 'totalArticles', label: 'Artikel gesamt', color: '#6366F1' },
      { key: 'lateArticles', label: 'Verspätet', color: '#EF4444' },
      { key: 'onTimeArticles', label: 'Pünktlich', color: '#10B981' },
    ],
    chart: {
      title: 'Lieferstatus',
      bars: [
        { key: 'onTimeArticles', label: 'Pünktlich', color: '#10B981' },
        { key: 'lateArticles', label: 'Verspätet', color: '#EF4444' },
      ],
    },
    detailColumns: [],
  },
  'job:teamshop_refund': {
    cards: [
      { key: 'totalJobs', label: 'Läufe gesamt', color: '#3B82F6' },
      { key: 'totalOrders', label: 'Aufträge gesamt', color: '#6366F1' },
      { key: 'berechnungsgrundlageBrutto', label: 'Basis Brutto \u20ac', color: '#8B5CF6' },
      { key: 'refundAmount', label: 'Rückvergütung \u20ac', color: '#10B981' },
    ],
    chart: {
      title: 'Beträge',
      bars: [
        { key: 'netto', label: 'Netto \u20ac', color: '#6366F1' },
        { key: 'refundAmount', label: 'Rückvergütung \u20ac', color: '#10B981' },
      ],
    },
    detailColumns: [],
  },
  'job:sales_analysis': {
    cards: [
      { key: 'totalJobs', label: 'Läufe gesamt', color: '#3B82F6' },
      { key: 'totalVariations', label: 'Varianten analysiert', color: '#6366F1' },
      { key: 'totalSold', label: 'Einheiten verkauft', color: '#10B981' },
      { key: 'totalRevenue', label: 'Umsatz Brutto \u20ac', color: '#8B5CF6' },
    ],
    detailColumns: [],
  },
  'job:vororder_analysis': {
    cards: [
      { key: 'totalJobs', label: 'Läufe gesamt', color: '#3B82F6' },
      { key: 'totalArticles', label: 'Artikel gesamt', color: '#6366F1' },
      { key: 'totalCritical', label: 'Kritisch', color: '#EF4444' },
      { key: 'totalOk', label: 'OK', color: '#10B981' },
    ],
    chart: {
      title: 'Artikel-Status',
      bars: [
        { key: 'totalOk', label: 'OK', color: '#10B981' },
        { key: 'totalWarning', label: 'Warnung', color: '#F59E0B' },
        { key: 'totalCritical', label: 'Kritisch', color: '#EF4444' },
        { key: 'totalNotFound', label: 'Nicht gefunden', color: '#9CA3AF' },
      ],
    },
    detailColumns: [],
  },
  'job:rating': {
    cards: [
      { key: 'totalJobs', label: 'Läufe gesamt', color: '#3B82F6' },
      { key: 'successRate', label: 'Erfolgsrate %', color: '#8B5CF6' },
      { key: 'contactsRated', label: 'Bewertet', color: '#10B981' },
      { key: 'fiveStarCount', label: '5 Sterne', color: '#F59E0B' },
    ],
    chart: {
      title: 'Bewertungsergebnis',
      bars: [
        { key: 'contactsRated', label: 'Bewertet (Batch + Save)', color: '#10B981' },
        { key: 'fiveStarCount', label: '5 Sterne (nur Batch)', color: '#F59E0B' },
      ],
    },
    detailColumns: [],
  },
};

// Fallback generische Stat-Config für unbekannte Job-Typen
const JOB_STAT_CONFIG: FlowStatConfig = {
  cards: [
    { key: 'totalJobs', label: 'Gesamt', color: '#3B82F6' },
    { key: 'completed', label: 'Erfolgreich', color: '#10B981' },
    { key: 'failed', label: 'Fehlgeschlagen', color: '#EF4444' },
    { key: 'successRate', label: 'Erfolgsrate %', color: '#8B5CF6' },
  ],
  chart: {
    title: 'Ergebnis',
    bars: [
      { key: 'completed', label: 'Erfolgreich', color: '#10B981' },
      { key: 'failed', label: 'Fehlgeschlagen', color: '#EF4444' },
    ],
  },
  detailColumns: [],
};

// Job-Historie → StatisticsData konvertieren
function jobsToStats(jobs: any[], jobType: string): StatisticsData {
  const completed = jobs.filter(j => j.status === 'completed').length;
  const failed = jobs.filter(j => j.status === 'failed').length;
  const total = jobs.length;

  const aggregated: Record<string, any> = {
    totalJobs: total,
    completed,
    failed,
    successRate: total > 0 ? Math.round((completed / total) * 100) : 0,
  };

  if (jobType === 'huck_price_upload' || jobType === 'helo_price_upload') {
    let pricesUpdated = 0, pricesFailed = 0;
    for (const j of jobs) {
      if (j.resultSummary) {
        pricesUpdated += j.resultSummary.success || 0;
        pricesFailed += j.resultSummary.failed || 0;
      }
    }
    aggregated.pricesUpdated = pricesUpdated;
    aggregated.pricesFailed = pricesFailed;
  } else if (jobType === 'sporttore_import') {
    let totalRows = 0, successCount = 0, errorCount = 0;
    for (const j of jobs) {
      if (j.resultSummary) {
        totalRows += j.resultSummary.totalRows || 0;
        successCount += j.resultSummary.successCount || 0;
        errorCount += j.resultSummary.errorCount || 0;
      }
    }
    aggregated.totalRows = totalRows;
    aggregated.successCount = successCount;
    aggregated.errorCount = errorCount;
  } else if (jobType === 'invoice_check_sporttore' || jobType === 'invoice_check_hela') {
    let totalChecked = 0, matched = 0, mismatched = 0, pdfNotFound = 0;
    for (const j of jobs) {
      if (j.resultSummary) {
        totalChecked += j.resultSummary.totalChecked || 0;
        matched += j.resultSummary.matched || 0;
        mismatched += j.resultSummary.mismatched || 0;
        pdfNotFound += j.resultSummary.pdfNotFound || 0;
      }
    }
    aggregated.totalChecked = totalChecked;
    aggregated.matched = matched;
    aggregated.mismatched = mismatched;
    aggregated.pdfNotFound = pdfNotFound;
  } else if (jobType === 'jako_analysis') {
    let totalArticles = 0, lateArticles = 0, onTimeArticles = 0;
    for (const j of jobs) {
      if (j.resultSummary) {
        totalArticles += j.resultSummary.totalArticles || 0;
        lateArticles += j.resultSummary.lateArticles || 0;
        onTimeArticles += j.resultSummary.onTimeArticles || 0;
      }
    }
    aggregated.totalArticles = totalArticles;
    aggregated.lateArticles = lateArticles;
    aggregated.onTimeArticles = onTimeArticles;
  } else if (jobType === 'teamshop_refund') {
    let totalOrders = 0, berechnungsgrundlageBrutto = 0, netto = 0, refundAmount = 0;
    for (const j of jobs) {
      if (j.resultSummary) {
        totalOrders += j.resultSummary.totalOrders || 0;
        berechnungsgrundlageBrutto += j.resultSummary.berechnungsgrundlageBrutto || 0;
        netto += j.resultSummary.netto || 0;
        refundAmount += j.resultSummary.refundAmount || 0;
      }
    }
    aggregated.totalOrders = totalOrders;
    aggregated.berechnungsgrundlageBrutto = Math.round(berechnungsgrundlageBrutto * 100) / 100;
    aggregated.netto = Math.round(netto * 100) / 100;
    aggregated.refundAmount = Math.round(refundAmount * 100) / 100;
  } else if (jobType === 'sales_analysis') {
    let totalVariations = 0, totalSold = 0, totalRevenue = 0;
    for (const j of jobs) {
      if (j.resultSummary) {
        totalVariations += j.resultSummary.totalVariations || 0;
        totalSold += j.resultSummary.totalSold || 0;
        totalRevenue += j.resultSummary.totalRevenue || 0;
      }
    }
    aggregated.totalVariations = totalVariations;
    aggregated.totalSold = totalSold;
    aggregated.totalRevenue = Math.round(totalRevenue * 100) / 100;
  } else if (jobType === 'vororder_analysis') {
    let totalArticles = 0, totalCritical = 0, totalWarning = 0, totalOk = 0, totalNotFound = 0;
    for (const j of jobs) {
      if (j.resultSummary) {
        totalArticles += j.resultSummary.totalArticles || 0;
        totalCritical += j.resultSummary.totalCritical || 0;
        totalWarning += j.resultSummary.totalWarning || 0;
        totalOk += j.resultSummary.totalOk || 0;
        totalNotFound += j.resultSummary.totalNotFound || 0;
      }
    }
    aggregated.totalArticles = totalArticles;
    aggregated.totalCritical = totalCritical;
    aggregated.totalWarning = totalWarning;
    aggregated.totalOk = totalOk;
    aggregated.totalNotFound = totalNotFound;
  } else if (jobType === 'rating') {
    let contactsChecked = 0, contactsRated = 0, fiveStarCount = 0, ratingsSubmitted = 0;
    for (const j of jobs) {
      if (j.resultSummary) {
        contactsChecked += j.resultSummary.contactsChecked || 0;
        contactsRated += j.resultSummary.contactsRated || 0;
        fiveStarCount += j.resultSummary.fiveStarCount || 0;
        ratingsSubmitted += j.resultSummary.ratingsSubmitted || 0;
      }
    }
    aggregated.contactsChecked = contactsChecked;
    aggregated.contactsRated = contactsRated + ratingsSubmitted;
    aggregated.fiveStarCount = fiveStarCount;
  }

  return {
    aggregated,
    recentRuns: jobs.slice(0, 50).map(j => ({
      id: String(j.id),
      status: j.status,
      startedAt: j.startedAt || j.createdAt,
      completedAt: j.completedAt,
      durationMs: j.startedAt && j.completedAt
        ? new Date(j.completedAt).getTime() - new Date(j.startedAt).getTime()
        : null,
      resultSummary: j.resultSummary,
      params: null,
    })),
    totalRuns: total,
  };
}

const FlowStatisticsTab: React.FC = () => {
  const [flows, setFlows] = useState<FlowCatalogEntry[]>([]);
  const [selectedFlow, setSelectedFlow] = useState<string>('');
  const [days, setDays] = useState<number>(30);
  const [stats, setStats] = useState<StatisticsData | null>(null);
  const [loading, setLoading] = useState(false);

  // Flow-Katalog laden
  useEffect(() => {
    api.flows.getCatalog().then(res => {
      const catalog = res.data || [];
      setFlows(catalog);
      if (catalog.length > 0 && !selectedFlow) {
        setSelectedFlow(catalog[0].id);
      }
    }).catch(() => {});
  }, []);

  // Statistiken laden
  const loadStats = useCallback(() => {
    if (!selectedFlow) return;
    setLoading(true);

    if (isJobType(selectedFlow)) {
      const jobType = selectedFlow.replace('job:', '');
      api.flows.getJobHistory({ jobType, limit: 500, offset: 0 }).then(res => {
        if (res.success) {
          let jobs = res.data.jobs || [];
          if (days > 0) {
            const cutoff = new Date();
            cutoff.setDate(cutoff.getDate() - days);
            jobs = jobs.filter((j: any) => new Date(j.createdAt) >= cutoff);
          } else if (days < 0) {
            const year = Math.abs(days);
            jobs = jobs.filter((j: any) => new Date(j.createdAt).getFullYear() === year);
          }
          setStats(jobsToStats(jobs, jobType));
        } else {
          setStats(null);
        }
      }).catch(() => setStats(null)).finally(() => setLoading(false));
    } else {
      api.flows.getStatistics(selectedFlow, days).then(res => {
        setStats(res.data);
      }).catch(() => {
        setStats(null);
      }).finally(() => setLoading(false));
    }
  }, [selectedFlow, days]);

  useEffect(() => { loadStats(); }, [loadStats]);

  const config = isJobType(selectedFlow)
    ? (JOB_STAT_CONFIGS[selectedFlow] || JOB_STAT_CONFIG)
    : getFlowStatConfig(selectedFlow, stats?.aggregated);

  return (
    <div>
      {/* Filter-Bar */}
      <div className="flex gap-3 mb-4 items-center">
        <div className="flex items-center gap-1.5">
          <BarChart2 size={16} className="text-gray-400" />
          <select
            value={selectedFlow}
            onChange={e => setSelectedFlow(e.target.value)}
            className="border border-[#DAD9D9] rounded px-3 py-1.5 text-xs text-[#1A1A1A] bg-white"
          >
            <optgroup label="Flows">
              {flows.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </optgroup>
            <optgroup label="Tools">
              {jobTypes.map(jt => (
                <option key={jt} value={`job:${jt}`}>{JOB_TYPE_LABELS[jt]}</option>
              ))}
            </optgroup>
          </select>
        </div>
        <select
          value={days}
          onChange={e => setDays(Number(e.target.value))}
          className="border border-[#DAD9D9] rounded px-3 py-1.5 text-xs text-[#1A1A1A] bg-white"
        >
          <option value={7}>Letzte 7 Tage</option>
          <option value={30}>Letzte 30 Tage</option>
          <option value={90}>Letzte 90 Tage</option>
          <option value={180}>Letzte 6 Monate</option>
          <option value={270}>Letzte 9 Monate</option>
          <option value={365}>Letzte 12 Monate</option>
          <option value={-2025}>Jahr 2025</option>
          <option value={-2026}>Jahr 2026</option>
          <option value={-2027}>Jahr 2027</option>
          <option value={0}>Alle</option>
        </select>
        {stats && (
          <span className="text-xs text-gray-400">
            {stats.totalRuns} Ausführung{stats.totalRuns !== 1 ? 'en' : ''}
          </span>
        )}
      </div>

      {loading && <div className="p-8 text-center text-gray-400">Laden...</div>}

      {!loading && stats && (
        <>
          {(() => {
            const displayData = selectedFlow === 'kundenbewertung'
              ? { ...stats.aggregated, cycle: stats.cycle ?? null, uniqueRatings: stats.uniqueRatings ?? null }
              : stats.aggregated;
            return (
              <>
                <MetricCards config={config} data={displayData} />
                {selectedFlow === 'kundenbewertung' && stats.cycle && (
                  <CycleProgressBar cycle={stats.cycle} />
                )}
                <HorizontalBarChart config={config} data={stats.aggregated} />
                <RecentRunsTable runs={stats.recentRuns} flowId={isJobType(selectedFlow) ? '' : selectedFlow} config={config} onDelete={() => loadStats()} />
              </>
            );
          })()}
        </>
      )}

      {!loading && !stats && selectedFlow && (
        <div className="p-8 text-center text-gray-400 text-xs">
          Keine Statistiken verfügbar
        </div>
      )}
    </div>
  );
};

export default FlowStatisticsTab;
